import { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"

import { useBookingContext } from "modules/Booking/BookingContext"
import { PaymentWidget } from "components"
import { AfterPaymentPage } from "modules/Booking/AfterPaymentPage"
import { Analytics } from "utils/analytics"
import { SESSION_TYPE } from "modules/Booking/common/constants"
import { ErrorPage } from "modules/Booking/ErrorPage"
import { Spinner } from "components/Spinner"
import { TryAgainPage } from "modules/Booking/TryAgainPage"
import { BookingStatusMap, PaymentSystemType } from "utils/interfaces"
import { getParameterFromUrl, removeUrlParameter } from "utils/helpers"
import { INSTANT_WIDGET, PAYMENT_DONE_PARAMETER } from "modules/BookingCommon/common/constants"
import { TIME_REQUEST_STATUSES, timeRequestStatusesSuccess } from "utils/constants"
import {
    createdTimeRequestLoader,
    createdBookingLoader,
    createdDepositLoader,
} from "modules/BookingCommon/common/queries"
import {
    DEPOSIT_STATUSES,
    depositStatusesSuccess,
} from "modules/Booking/common/constants/depositStatuses"
import { postFailedBooking } from "modules/Booking/PaymentPage/api"
import {
    bookingStatusPaymentInProcess,
    paymentStateType,
} from "modules/Booking/PaymentPage/constants"
import { ErrorPageWrapper } from "modules/Booking/PaymentPage/ErrorPageWrapper"
import { paymentPageProps } from "modules/Booking/PaymentPage/helper"

function cleanUrl() {
    const newUrl = removeUrlParameter(window.location.href, INSTANT_WIDGET)
    newUrl && window.history.replaceState({}, "", newUrl)
}

export function PaymentPage() {
    const [{ hasSlots }] = useBookingContext()

    const bookingHash = createdBookingLoader.getHash()
    const bookingDetailsQuery = createdBookingLoader.useGet(bookingHash)
    const bookingDetails = bookingDetailsQuery.data
    const bookingStatus = bookingDetailsQuery.data?.status

    const depositHash = createdDepositLoader.getHash()
    const depositQuery = createdDepositLoader.useGet(depositHash)
    const depositStatus = depositQuery.data?.status

    const timeRequestHash = createdTimeRequestLoader.getHash()
    const timeRequestQuery = createdTimeRequestLoader.useGet(timeRequestHash)
    const timeRequestStatus = timeRequestQuery.data?.status

    const instant_widget = getParameterFromUrl(INSTANT_WIDGET) === "1"
    const [paymentState, setPaymentState] = useState<paymentStateType>(paymentStateType.wait)
    const payment_done = getParameterFromUrl(PAYMENT_DONE_PARAMETER) === "1"
    const routeLocation = useLocation()

    const sessionType = (function () {
        if (depositHash) {
            return SESSION_TYPE.DEPOSIT
        }
        if (timeRequestHash) {
            return SESSION_TYPE.TIME_REQUEST
        }
        return SESSION_TYPE.DEFAULT
    })()

    useEffect(() => {
        if (
            (
                routeLocation.state as {
                    reloadPage?: string
                }
            )?.reloadPage
        ) {
            window.location.reload()
        }
    }, [])

    useEffect(() => {
        const query = (function () {
            if (bookingHash) {
                return bookingDetailsQuery
            }
            if (depositHash) {
                return depositQuery
            }
            if (timeRequestHash) {
                return timeRequestQuery
            }
        })()
        if (!query) return

        const status = query.data?.status

        if (!status) return

        if (query.isFetching) {
            setPaymentState(paymentStateType.wait)
        } else if (bookingStatusPaymentInProcess.includes(status) && payment_done) {
            setPaymentState(paymentStateType.paymentInProcess)
        } else if (
            status === BookingStatusMap.PAID ||
            timeRequestStatusesSuccess.includes(timeRequestStatus as TIME_REQUEST_STATUSES) ||
            depositStatusesSuccess.includes(depositStatus as DEPOSIT_STATUSES)
        ) {
            setPaymentState(paymentStateType.afterPayment)
        } else if (status === BookingStatusMap.CANCELLED) {
            setPaymentState(paymentStateType.cancelled)
        } else if (timeRequestStatus === TIME_REQUEST_STATUSES.CANCELLED_WITHOUT_PAYMENT) {
            setPaymentState(paymentStateType.cancelledWithoutPayment)
        } else if (instant_widget) {
            setPaymentState(paymentStateType.renderWidget)
        } else {
            setPaymentState(paymentStateType.tryAgain)
        }
    }, [bookingStatus, timeRequestStatus, depositStatus])

    useEffect(() => {
        if (sessionType === SESSION_TYPE.TIME_REQUEST) {
            if (hasSlots) {
                Analytics.user_went_to_payment_after_time_request_modal()
            } else {
                Analytics.user_went_to_payment_after_time_request_no_slots_modal()
            }
        }
    }, [])

    useEffect(() => {
        if (paymentState === paymentStateType.renderWidget) {
            cleanUrl()
            history.pushState(null, "", removeUrlParameter(location.href, INSTANT_WIDGET))
            window.addEventListener("popstate", () => {
                setPaymentState(paymentStateType.tryAgain)
                history.pushState(null, "", removeUrlParameter(location.href, INSTANT_WIDGET))
            })
        }
    }, [paymentState])

    function onPaymentError() {
        setPaymentState(paymentStateType.error)
    }

    function onPaymentFailure(reason: unknown) {
        if (sessionType === SESSION_TYPE.DEFAULT && !!bookingDetails) {
            postFailedBooking(bookingDetails, reason)
        }
        setPaymentState(paymentStateType.tryAgain)
    }

    const props = paymentPageProps(
        sessionType,
        timeRequestQuery.data,
        bookingDetails,
        depositQuery.data
    )

    switch (paymentState) {
        case paymentStateType.error:
            return <ErrorPageWrapper />
        case paymentStateType.cancelled:
            return (
                <ErrorPage
                    headline="Время бронирования истекло"
                    secondary="Пожалуйста, выберите другое время."
                />
            )
        case paymentStateType.afterPayment:
            return <AfterPaymentPage />
        case paymentStateType.renderWidget:
            return (
                <div className="redesign-container">
                    <PaymentWidget
                        payment_system={props?.paymentSystem}
                        booking_id={props?.bookingId?.toString() || ""}
                        order_id={props?.orderId?.toString() || ""}
                        order_hash={props?.orderHash?.toString() || ""}
                        psy={
                            depositQuery.data
                                ? {
                                      hashId: depositQuery.data.psy.hash_id,
                                      displayName: depositQuery.data.psy.display_name,
                                  }
                                : PaymentWidget.getPsyData(
                                      bookingDetails?.psy || timeRequestQuery.data?.psy
                                  )
                        }
                        booking_hash={bookingDetails?.booking_hash || ""}
                        deposit_hash={depositHash}
                        time_request_hash={timeRequestHash}
                        onError={onPaymentError}
                        onFail={onPaymentFailure}
                        price={props?.price?.toString() || ""}
                        price_usd={props?.priceUsd?.toString() || ""}
                        sessionType={sessionType}
                        changePaymentPageState={(state: paymentStateType) => setPaymentState(state)}
                    />
                </div>
            )
        case paymentStateType.tryAgain:
            return (
                <div className="redesign-container">
                    <TryAgainPage
                        changePaymentPageState={(state: paymentStateType) => setPaymentState(state)}
                    />
                </div>
            )
        case paymentStateType.paymentInProcess:
            return (
                <div className="redesign-container">
                    <Spinner text="Ожидание завершения оплаты" />
                </div>
            )
        case paymentStateType.cancelledWithoutPayment:
            return (
                <ErrorPage
                    headline="Произошла ошибка"
                    secondary="Обратитесь в службу поддержки."
                    buttonText="Выбрать время"
                />
            )
        case paymentStateType.wait:
            if (props?.paymentSystem === PaymentSystemType.CLOUDPAYMENTS)
                return <div className="is-flex-grow-1" />
            return <Spinner />
        default:
            return <ErrorPageWrapper />
    }
}
